import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Text,
  IconButton,
  VStack,
  HStack,
  Collapse,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FaFolder,
  FaFolderOpen,
  FaFile,
  FaPlus,
  FaTrash,
  FaEdit,
  FaFolderPlus,
  FaUpload,
} from 'react-icons/fa';

const FileExplorer = ({
  files,
  selectedFile,
  onFileSelect,
  onRenameItem,
  onDeleteItem,
  onCreateFile,
  onCreateFolder,
  onImportFile,
  onImportFolder,
  showActions = true,
}) => {
  const [expandedFolders, setExpandedFolders] = useState({});
  const fileInputRefs = useRef({});
  const folderInputRefs = useRef({});
  const bgSelected = useColorModeValue('navy.50', 'navy.600');
  const bgHover = useColorModeValue('navy.50', 'navy.600');
  const bg = useColorModeValue('navy.200', 'navy.600');
  const userAuthority = localStorage.getItem('userAuthority');
  const isTenantAdmin = userAuthority === 'TENANT_ADMIN';

  const toggleFolder = (id) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    const folderMap = {};
    files.forEach((file) => {
      if (file.type === 'folder') {
        folderMap[file.id] = true;
      }
    });
    setExpandedFolders(folderMap);
  }, [files]);

  useEffect(() => {
    if (!selectedFile) {
      const mainPy = files.find(
        (f) => f.name === 'main.py' && f.type === 'file',
      );
      if (mainPy) {
        onFileSelect(mainPy);
      }
    }
  }, [files, selectedFile, onFileSelect]);

  const renderTree = (item, level) => {
    const isFolder = item.type === 'folder';
    const isSelected = selectedFile?.id === item.id;
    const isExpanded = expandedFolders[item.id];

    const folderClosedColor = 'orange.400';
    const folderOpenColor = 'orange.600';
    const fileColor = 'teal.500';

    const handleRename = () => onRenameItem(item);
    const handleDelete = () => onDeleteItem(item);
    const handleAddFile = () => {
      onCreateFile(item);
      if (!isExpanded) toggleFolder(item.id);
    };
    const handleAddFolder = () => {
      onCreateFolder(item);
      if (!isExpanded) toggleFolder(item.id);
    };

    // Ensure refs for each folder
    if (isFolder) {
      if (!fileInputRefs.current[item.id]) {
        fileInputRefs.current[item.id] = React.createRef();
      }
      if (!folderInputRefs.current[item.id]) {
        folderInputRefs.current[item.id] = React.createRef();
      }
    }

    const handleImportClick = () => {
      fileInputRefs.current[item.id]?.current?.click();
    };

    const handleImportChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        onImportFile(file, item); // single file
        event.target.value = '';
      }
    };

    const handleFolderImportClick = () => {
      folderInputRefs.current[item.id]?.current?.click();
    };

    const handleFolderImportChange = (event) => {
      onImportFolder(event.target.files, item); // multiple files
      event.target.value = '';
    };

    return (
      <Box
        key={item.id}
        position="relative"
        pl={level * 6}
        mb={1}
        borderRadius="md"
        overflow="visible"
      >
        {level > 0 && (
          <Box
            position="absolute"
            top={0}
            left={level * 6 - 3}
            height="100%"
            width="1px"
            bg={bg}
            zIndex={0}
          />
        )}

        <HStack
          spacing={3}
          align="center"
          bg={isSelected ? bgSelected : 'transparent'}
          cursor="pointer"
          px={2}
          py={1}
          borderRadius="md"
          _hover={{ bg: isSelected ? bgSelected : bgHover }}
          onClick={() =>
            isFolder ? toggleFolder(item.id) : onFileSelect(item)
          }
          userSelect="none"
          position="relative"
          zIndex={1}
        >
          {level > 0 && (
            <Box
              position="absolute"
              top="50%"
              left={level * 6 - 6}
              width="6px"
              height="1px"
              bg={bg}
              transform="translateY(-50%)"
              zIndex={0}
            />
          )}

          {isFolder ? (
            isExpanded ? (
              <Box color={folderOpenColor} fontSize="lg" minWidth={5}>
                <FaFolderOpen />
              </Box>
            ) : (
              <Box color={folderClosedColor} fontSize="lg" minWidth={5}>
                <FaFolder />
              </Box>
            )
          ) : (
            <Box color={fileColor} fontSize="lg" minWidth={5}>
              <FaFile />
            </Box>
          )}

          <Tooltip label={item.name} placement="top-start" hasArrow>
            <Text
              fontWeight={isSelected ? 'semibold' : 'normal'}
              noOfLines={1}
              flex="1"
              ml={1}
              color={
                isSelected
                  ? undefined
                  : isFolder
                    ? folderClosedColor
                    : fileColor
              }
            >
              {item.name}
            </Text>
          </Tooltip>
          {showActions && isTenantAdmin && (
            <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
              <Tooltip label="Rename" hasArrow>
                <IconButton
                  icon={<FaEdit />}
                  size="xs"
                  aria-label="Rename"
                  variant="ghost"
                  onClick={handleRename}
                />
              </Tooltip>

              <Tooltip label="Delete" hasArrow>
                <IconButton
                  icon={<FaTrash />}
                  size="xs"
                  aria-label="Delete"
                  variant="ghost"
                  colorScheme="red"
                  onClick={handleDelete}
                />
              </Tooltip>

              {isFolder && (
                <>
                  <Tooltip label="Add File" hasArrow>
                    <IconButton
                      icon={<FaPlus />}
                      size="xs"
                      aria-label="Add File"
                      variant="ghost"
                      onClick={handleAddFile}
                    />
                  </Tooltip>

                  <Tooltip label="Add Folder" hasArrow>
                    <IconButton
                      icon={<FaFolderPlus />}
                      size="xs"
                      aria-label="Add Folder"
                      variant="ghost"
                      onClick={handleAddFolder}
                    />
                  </Tooltip>

                  <Tooltip label="Import File" hasArrow>
                    <IconButton
                      icon={<FaUpload />}
                      size="xs"
                      aria-label="Import File"
                      variant="ghost"
                      onClick={handleImportClick}
                    />
                  </Tooltip>

                  <Tooltip label="Import Folder" hasArrow>
                    <IconButton
                      icon={<FaFolderOpen />}
                      size="xs"
                      aria-label="Import Folder"
                      variant="ghost"
                      onClick={handleFolderImportClick}
                    />
                  </Tooltip>

                  {/* Hidden Inputs */}
                  <input
                    type="file"
                    ref={fileInputRefs.current[item.id]}
                    onChange={handleImportChange}
                    style={{ display: 'none' }}
                  />

                  <input
                    type="file"
                    ref={folderInputRefs.current[item.id]}
                    onChange={handleFolderImportChange}
                    webkitdirectory="true"
                    multiple
                    style={{ display: 'none' }}
                  />
                </>
              )}
            </HStack>
          )}
        </HStack>

        {isFolder && (
          <Collapse in={isExpanded} animateOpacity>
            <VStack align="start" mt={1} spacing={0}>
              {files
                .filter((f) => f.parentId === item.id)
                .map((child) => renderTree(child, level + 1))}
            </VStack>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {files
        .filter((file) => file.parentId === null)
        .map((item) => renderTree(item, 0))}
    </Box>
  );
};

export default FileExplorer;
